import isNetworkError from 'is-network-error'
import { type KyInstance, isHTTPError, isTimeoutError } from 'ky'
import type {
  PaymentPlatform,
  PaymentSystemIdByPlatform,
  ProductMetadata,
  PurchasePayload,
  Result,
} from './types'

export interface SubscribeOptions<Product extends ProductMetadata> {
  api: {
    service: KyInstance
    receipt: KyInstance
  }
  platform: PaymentPlatform
  userId: number
  product: Product
  nativePurchase: (
    payload: PurchasePayload,
  ) => Promise<
    Result<
      { transaction_id: string },
      | 'interrupted'
      | 'pending'
      | 'product not found'
      | 'invalid transaction'
      | 'unknown'
    >
  >
  closeTransaction: (payload: {
    transaction_id: string
  }) => Promise<void> | void
  receiptSystemAccountId: string | null
  /** Subscription offer to apply — native ≥ 5.19.0 selects the offer by id. */
  offerId?: string
  /** iOS promotional-offer signature; Android omits it. */
  offerCompactJws?: string
  shouldResolve?: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus
  /**
   * The active subscription expiry captured before purchase. When omitted, the
   * caller guarantees that the user is not already subscribed.
   */
  currentSubscriptionExpiresAt?: string | null
  /**
   * Debug option to simulate AfterPaymentError for testing restore flow.
   * When true, throws AfterPaymentError after native purchase succeeds.
   */
  debugSimulateAfterPaymentError?: boolean
  onStepError?: (
    error: unknown,
    context: {
      step:
        | 'create_order'
        | 'native_purchase'
        | 'mark_processing'
        | 'post_transaction'
        | 'close_transaction'
        | 'poll_service_level'
        | 'recover_interrupted_purchase'
      purchaseId?: number
      transactionId?: string
    },
  ) => void
  logEvent?: (payload: {
    event_name: string
    event_params?: Record<string, string>
  }) => Promise<void>
}

export const PaymentSystemID = {
  PingXX: 7,
  GooglePlay: 8,
  AppStoreStoreKit1: 9,
  AppStoreStoreKit2: 11,
}

export const paymentSystemIdByPlatform: PaymentSystemIdByPlatform = {
  china_android: PaymentSystemID.PingXX,
  googleplay_android: PaymentSystemID.GooglePlay,
  ios: PaymentSystemID.AppStoreStoreKit2,
}

export async function subscribe<Product extends ProductMetadata>({
  api,
  platform,
  userId,
  product,
  nativePurchase,
  closeTransaction,
  receiptSystemAccountId,
  offerId,
  offerCompactJws,
  shouldResolve,
  currentSubscriptionExpiresAt,
  debugSimulateAfterPaymentError,
  onStepError,
  logEvent,
}: SubscribeOptions<Product>): Promise<SubscriptionServiceLevelStatus | null> {
  // 1. Create order
  let purchaseId: number
  try {
    const response = await api.service
      .post(`store/users/${userId}/purchase-right`, {
        json: {
          product_id: product.product_id,
          iap_sku: product.sku,
        },
      })
      .json<PostPurchaseResponse>()
    purchaseId = response.results[0].purchase.id
  } catch (error) {
    const code = classifyErrorCode(error)
    let componentName = ''
    switch (code) {
      case 'network':
      case 'timeout':
        componentName = 'internet_issue'
        break
      default:
        componentName = `error_${code}`
    }
    void logEvent?.({
      event_name: 'component_event',
      event_params: {
        type: 'dialog',
        app_action: 'show',
        component_name: componentName,
      },
    })
    onStepError?.(error, { step: 'create_order' })
    throw error
  }

  const postTransaction = async (transactionId: string) => {
    let receiptRequestUrl = `projects/6/users/${userId}`
    let data = {}
    if (platform === 'china_android') {
      receiptRequestUrl += '/pending_iap_receipts'
      data = { receipt_data: transactionId }
    } else {
      receiptRequestUrl += '/subscriptions'
      data = { transaction_id: transactionId }
    }
    await api.receipt.post(receiptRequestUrl, {
      json: {
        ...data,
        sku_id: product.sku,
        payment_system_id: paymentSystemIdByPlatform[platform],
      },
    })
  }

  const executeNativePurchase = async (): Promise<
    string | SubscriptionServiceLevelStatus
  > => {
    const payload: PurchasePayload =
      product.product_type === 'subscription'
        ? {
            product_type: 'subscription',
            product_id: product.product_id,
            sku: product.sku,
            offer_id: offerId,
            offer_compact_jws: offerCompactJws,
            receipt_system_account_id: receiptSystemAccountId,
          }
        : {
            product_type: 'consumable',
            product_id: product.product_id,
            sku: product.sku,
            receipt_system_account_id: receiptSystemAccountId,
          }
    const result = await nativePurchase(payload)
    if (!result.success) {
      if (
        platform === 'china_android' &&
        product.product_type === 'subscription' &&
        result.error === 'interrupted'
      ) {
        const recovery = await recoverInterruptedSubscription(
          api.service,
          userId,
          currentSubscriptionExpiresAt,
        )
        if (recovery.status) return recovery.status
        if (recovery.lastRequestError !== undefined) {
          onStepError?.(recovery.lastRequestError, {
            step: 'recover_interrupted_purchase',
            purchaseId,
          })
        }
      }
      // Once China Android recovery is exhausted, 'interrupted' follows the
      // existing cancelled-purchase path. Every other store error remains a
      // failed purchase. See ADR 0005 and its China Android exception, ADR 0008.
      const markAction =
        result.error === 'interrupted' ? 'mark-cancelled' : 'mark-failed'
      void api.service
        .post(`store/users/${userId}/purchase/${purchaseId}/${markAction}`)
        .catch(() => {})
      const error = new NativeStoreError(result.error as NativeStoreErrorType)
      void logEvent?.({
        event_name: 'component_event',
        event_params: {
          type: 'dialog',
          app_action: 'show',
          component_name: `sub_paid_fail_${error.message.replace(/ /g, '_')}`,
        },
      })
      onStepError?.(error, { step: 'native_purchase', purchaseId })
      throw error
    }
    return result.data.transaction_id
  }

  // 2. Call native app to let user pay
  const initialPurchase = await executeNativePurchase()
  if (typeof initialPurchase !== 'string') return initialPurchase
  let transactionId = initialPurchase

  // Debug: simulate AfterPaymentError for testing restore flow
  if (debugSimulateAfterPaymentError) {
    throw new AfterPaymentError(purchaseId, transactionId, 'debug_simulation')
  }

  // 3. Mark purchase as processing
  try {
    await api.service.post(
      `store/users/${userId}/purchase/${purchaseId}/mark-processing`,
    )
  } catch (error) {
    onStepError?.(error, {
      step: 'mark_processing',
      purchaseId,
      transactionId,
    })
    throw new AfterPaymentError(purchaseId, transactionId, 'mark_processing')
  }

  // 4. Post transaction to receipt system server
  // A 409 means the receipt was already posted (e.g. by a previous interrupted attempt).
  // Close the stale transaction, get a fresh one from the native store, and retry once.
  try {
    await postTransaction(transactionId)
  } catch (error) {
    if (!shouldCloseTransactionAndRetry(error)) {
      onStepError?.(error, {
        step: 'post_transaction',
        purchaseId,
        transactionId,
      })
      throw new AfterPaymentError(purchaseId, transactionId, 'post_transaction')
    }
    try {
      await closeTransaction({ transaction_id: transactionId })
    } catch (e) {
      console.error('Failed to close transaction before retrying', e)
    }
    const retryPurchase = await executeNativePurchase()
    if (typeof retryPurchase !== 'string') return retryPurchase
    transactionId = retryPurchase
    try {
      await postTransaction(transactionId)
    } catch (retryError) {
      onStepError?.(retryError, {
        step: 'post_transaction',
        purchaseId,
        transactionId,
      })
      throw new AfterPaymentError(purchaseId, transactionId, 'post_transaction')
    }
  }

  // 5. Tell native app to close transaction
  try {
    await closeTransaction({ transaction_id: transactionId })
  } catch (error) {
    onStepError?.(error, {
      step: 'close_transaction',
      purchaseId,
      transactionId,
    })
  }

  // 6. Wait for service level to become subscription
  try {
    return await waitForSubscriptionStatus({
      serviceApi: api.service,
      userId,
      shouldResolve,
    })
  } catch (error) {
    onStepError?.(error, {
      step: 'poll_service_level',
      purchaseId,
      transactionId,
    })
    console.error('Failed to poll service level status', error)
    return null
  }
}

function classifyErrorCode(
  error: unknown,
): 'timeout' | 'network' | 'unknown' | (string & {}) {
  if (isHTTPError(error)) return String(error.response.status)
  if (isTimeoutError(error)) return 'timeout'
  if (isNetworkError(error as Error)) return 'network'
  return 'unknown'
}

function shouldCloseTransactionAndRetry(error: unknown): boolean {
  if (isHTTPError(error)) {
    return (
      error.response.status === 409 ||
      error.response.status === 410 ||
      error.response.status === 412
    )
  }
  return false
}

type NativeStoreErrorType =
  | 'interrupted'
  | 'pending'
  | 'product not found'
  | 'invalid transaction'
  | 'unknown'

export class NativeStoreError extends Error {
  constructor(message: NativeStoreErrorType) {
    super(message)
    this.name = 'NativeStoreError'
  }
}

export class AfterPaymentError extends Error {
  public purchaseId: number
  public transactionId: string | null
  public node: string
  constructor(purchaseId: number, transactionId: string | null, node: string) {
    super('Purchase failed')
    this.name = 'AfterPaymentError'
    this.purchaseId = purchaseId
    this.transactionId = transactionId
    this.node = node
  }
}

interface PostPurchaseResponse {
  results: {
    purchase: { id: number }
  }[]
}

const SERVICE_LEVEL_POLL_INTERVAL_MS = 100
const SERVICE_LEVEL_POLL_TIMEOUT_MS = 15_000
const INTERRUPTED_RECOVERY_DELAYS_MS = [0, 1_000, 2_000]
const INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS = 10_000

export type ServiceLevelStatus =
  | {
      service_level: 'guest' | 'signup' | 'premium_signup' | 'premium_guest'
    }
  | SubscriptionServiceLevelStatus

export interface SubscriptionServiceLevelStatus {
  service_level: 'subscription'
  subscription: {
    auto_renew_sku_id: string
    cancel_reason: string
    cancelled_at: string
    created_at: string
    expires_at: string
    id: number
    is_auto_renewing: boolean
    is_in_introductory: boolean
    is_in_trial: boolean
    payment_system_psid: number
    promotional_offer_identifier: string
    receiptable_id: number
    sku_id: string
    starts_at: string
    transaction_id: string
    user_id: number
  }
}

export async function waitForSubscriptionStatus({
  serviceApi,
  userId,
  intervalMs = SERVICE_LEVEL_POLL_INTERVAL_MS,
  timeoutMs = SERVICE_LEVEL_POLL_TIMEOUT_MS,
  signal,
  shouldResolve = isSubscriptionStatus,
}: {
  serviceApi: KyInstance
  userId: number
  intervalMs?: number
  timeoutMs?: number
  signal?: AbortSignal
  shouldResolve?: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus
}): Promise<SubscriptionServiceLevelStatus> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)
  const combinedSignal = mergeAbortSignals(signal, controller.signal)

  try {
    return await pollServiceLevelStatus(
      serviceApi,
      userId,
      combinedSignal,
      intervalMs,
      shouldResolve,
    )
  } catch (error) {
    if (combinedSignal?.aborted) {
      throw new Error('Polling service level timed out')
    }
    throw error
  } finally {
    clearTimeout(timeoutId)
  }
}

async function pollServiceLevelStatus(
  serviceApi: KyInstance,
  userId: number,
  signal: AbortSignal | undefined,
  intervalMs: number,
  shouldResolve: (
    status: ServiceLevelStatus,
  ) => status is SubscriptionServiceLevelStatus,
): Promise<SubscriptionServiceLevelStatus> {
  while (true) {
    if (signal?.aborted) {
      throw new Error('Polling service level aborted')
    }
    const status = await serviceApi
      .get(`user/users/${userId}/service-levels/status`, { signal })
      .json<ServiceLevelStatus>()
    if (shouldResolve(status)) {
      return status
    }
    await sleep(intervalMs, signal)
  }
}

function isSubscriptionStatus(
  status: ServiceLevelStatus,
): status is SubscriptionServiceLevelStatus {
  return status.service_level === 'subscription'
}

async function recoverInterruptedSubscription(
  serviceApi: KyInstance,
  userId: number,
  currentSubscriptionExpiresAt: string | null | undefined,
): Promise<{
  status: SubscriptionServiceLevelStatus | null
  lastRequestError?: unknown
}> {
  let lastRequestError: unknown
  for (const delayMs of INTERRUPTED_RECOVERY_DELAYS_MS) {
    if (delayMs > 0) await sleep(delayMs)
    try {
      const status = await serviceApi
        .get(`user/users/${userId}/service-levels/status`, {
          retry: 0,
          timeout: INTERRUPTED_RECOVERY_REQUEST_TIMEOUT_MS,
        })
        .json<ServiceLevelStatus>()
      if (isRecoveredSubscriptionStatus(status, currentSubscriptionExpiresAt)) {
        return { status }
      }
    } catch (error) {
      lastRequestError = error
      // A failed request consumes its attempt; a later attempt may still
      // observe the server-side PingXX recovery.
    }
  }
  return { status: null, lastRequestError }
}

function isRecoveredSubscriptionStatus(
  status: ServiceLevelStatus,
  currentSubscriptionExpiresAt: string | null | undefined,
): status is SubscriptionServiceLevelStatus {
  if (!isSubscriptionStatus(status)) return false
  if (currentSubscriptionExpiresAt == null) return true

  const recoveredExpiry = Date.parse(status.subscription.expires_at)
  const currentExpiry = Date.parse(currentSubscriptionExpiresAt)
  return (
    Number.isFinite(recoveredExpiry) &&
    Number.isFinite(currentExpiry) &&
    recoveredExpiry > currentExpiry
  )
}

function sleep(ms: number, signal?: AbortSignal) {
  return new Promise<void>((resolve, reject) => {
    const timer = setTimeout(resolve, ms)
    if (signal) {
      signal.addEventListener(
        'abort',
        () => {
          clearTimeout(timer)
          reject(new Error('Polling service level aborted'))
        },
        { once: true },
      )
    }
  })
}

function mergeAbortSignals(
  ...signals: (AbortSignal | undefined)[]
): AbortSignal | undefined {
  const activeSignals = signals.filter(Boolean) as AbortSignal[]
  if (activeSignals.length === 0) return undefined
  const controller = new AbortController()
  const onAbort = () => controller.abort()
  activeSignals.forEach((sig) => sig.addEventListener('abort', onAbort))
  if (activeSignals.some((sig) => sig.aborted)) {
    controller.abort()
  }
  return controller.signal
}
